import {
    Apps,
    Article,
    BusinessCenterOutlined,
    BusinessOutlined,
    ChatBubbleOutline,
    CodeOutlined,
    DiscountOutlined,
    EventNoteOutlined,
    EventOutlined,
    HelpOutlineOutlined,
    InstallDesktop,
    IntegrationInstructionsOutlined,
    KeyOutlined,
    ListAltOutlined,
    PaymentsOutlined,
    QrCode,
    QuizOutlined,
    SettingsApplicationsOutlined,
    ShoppingCartOutlined,
    StarOutline,
    Storefront,
    VideoLibraryOutlined,
    WebhookOutlined,
} from '@mui/icons-material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import GenAIcon from '@mui/icons-material/AutoAwesomeOutlined';
import AutoGraphOutlinedIcon from '@mui/icons-material/AutoGraphOutlined';
import BackgroundMFsIcon from '@mui/icons-material/BackupTableOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CampaignIcon from '@mui/icons-material/Campaign';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';
import DesktopMacOutlinedIcon from '@mui/icons-material/DesktopMacOutlined';
import DeviceHubOutlinedIcon from '@mui/icons-material/DeviceHubOutlined';
import LineAxisIcon from '@mui/icons-material/LineAxis';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import PhoneMissedOutlinedIcon from '@mui/icons-material/PhoneMissedOutlined';
import DeliveryIcon from '@mui/icons-material/PollOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import RecentActorsOutlinedIcon from '@mui/icons-material/RecentActorsOutlined';
import SaveAsOutlinedIcon from '@mui/icons-material/SaveAsOutlined';
import SsidChartOutlinedIcon from '@mui/icons-material/SsidChartOutlined';
import StarRateOutlinedIcon from '@mui/icons-material/StarRateOutlined';
import StreamingIcon from '@mui/icons-material/StreamOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import ConversationIcon from '@mui/icons-material/TextsmsOutlined';
import CommunicationIcon from '@mui/icons-material/TextSnippetOutlined';
import ThumbUpOffAltOutlinedIcon from '@mui/icons-material/ThumbUpOffAltOutlined';
import ICallIcon from 'common/assets/icons/iCallIcon';
import ILiveIcon from 'common/assets/icons/iLiveIcon';
import IVideoIcon from 'common/assets/icons/iVideoIcon';
import UserRole from 'common/enums/roles';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Route } from 'react-router-dom';
import { getUser, testFeatureEnabled } from 'redux/slices/authSlice';
import { getLicense } from 'redux/slices/licenseSlice';
import { getOrganization } from 'redux/slices/organizationSlice';
import QrConfirmation from 'views/QrConfirmation';
import SettingsKey from '../../app/profile/settings/utils/settingsTypes';
import { useAuthLicenseAddOns } from '../../common/features/licenseFeatures/useAuthLicenseAddOns';
import { UserPermissions } from '../../common/features/userPermissions/PermissionsEnum';
import { hasOnlyPermission, transformPermissions } from '../../common/features/userPermissions/util';
import { useSettingValue } from '../../common/hooks/useSettingValue';
import loadLazyComponent from '../../common/utils/loadLazyComponent';
import LazySuspense from '../lazy/LazySuspense';
import MicroFront from '../micro-front/MicroFront';

const RootRole = Object.freeze([UserRole.ROOT]);
const AdminRole = Object.freeze([UserRole.ADMIN]);

export function useMenuEntries() {
    const license = useSelector(getLicense);
    const user = useSelector(getUser);
    const organization = useSelector(getOrganization);
    const userPermissions = useMemo(() => transformPermissions(user?.permissions), [user?.permissions]);
    const isAuthorize = useAuthLicenseAddOns();
    const isTestFeatureEnabled = useSelector(testFeatureEnabled);
    const hideO2M = !license?.services?.oneToMany?.iLive;
    const hideShoppable = !license?.services?.oneToMany?.shoppable;
    const hideChannel = !license?.services?.oneToMany?.channel;
    const hideO2O = !license?.services?.oneToOne?.iCall;
    const hideIChat = !license?.services?.oneToOne?.iChat;
    const hideICalendar = !license?.services?.oneToOne?.iCalendar;
    const hideIEvents = !license?.services?.oneToOne?.iEvents;
    const hideIStore = !license?.services?.oneToOne?.iStore;
    const hideLeads = !license?.services?.leads;
    const hideCampaigns = !license?.services?.campaigns;
    const hideInteractions = hideO2M && hideO2O;
    const hideMedia = hideO2M && hideO2O;
    const hideImmersive = !license?.services?.immersive;

    const dashboardAvailableTimezones = useSettingValue(SettingsKey.DASHBOARD_AVAILABLE_TIMEZONES) || [];
    const enableInstallations = useSettingValue(SettingsKey.ENABLE_INSTALLATIONS_MENU);
    const enableNewInstallations = useSettingValue(SettingsKey.NEW_INSTALLATIONS);
    const calendarVersion = useSettingValue(SettingsKey.CALENDAR_VERSION) ?? 3;
    const hideLicense = license?.price === 0;
    const hideDashboard = dashboardAvailableTimezones?.length === 0;
    const genaiEnabled = useSettingValue(SettingsKey.ENABLE_GENAI);
    const newIChat = useSettingValue(SettingsKey.NEW_ICHAT);
    const isEnableTestDriveStripePayments = useSettingValue(SettingsKey.ENABLE_TEST_DRIVE_STRIPE_PAYMENTS);

    const LazyOneToManyView = loadLazyComponent(() => import('app/appViewsHandle/components/OneToManyViewHandle'));
    const LazyOneToOneView = loadLazyComponent(() => import('app/appViewsHandle/components/OneToOneViewHandle'));
    const LazyShoppableView = loadLazyComponent(() => import('app/appViewsHandle/components/ShoppableViewHandle'));
    const LazyIChatView = loadLazyComponent(() => import('app/appViewsHandle/components/IChatViewHandle'));

    const LazyMultimediaView = loadLazyComponent(() => import('app/appViewsHandle/components/MultimediaViewHandle'));
    const LazyEngagementView = loadLazyComponent(() => import('app/appViewsHandle/components/EngagementViewHandle'));
    const LazyProductsView = loadLazyComponent(() => import('app/appViewsHandle/components/ProductsViewHandle'));

    const LazySocialMediaView = loadLazyComponent(() => import('app/appViewsHandle/components/SocialMediaViewHandle'));
    const LazyTvChannelView = loadLazyComponent(() => import('app/appViewsHandle/components/TvChannelViewHandle'));
    const LazyTvChannelsConnectors = loadLazyComponent(() => import('app/tvChannel/TvChannelsConnectors'));
    const LazyEditTvChannelView = loadLazyComponent(() =>
        import('app/appViewsHandle/components/EditTvChannelViewHandle'),
    );
    // const LazyOneToOneDashboard = loadLazyComponent(() =>
    //     import('app/appViewsHandle/components/OneToOneDashboardViewHandle'),
    // );
    const LazyUsersView = loadLazyComponent(() => import('app/appViewsHandle/components/UsersViewHandle'));
    const LazyWebhooksView = loadLazyComponent(() => import('app/appViewsHandle/components/WebhooksViewHandle'));
    const LazyBillingView = loadLazyComponent(() => import('app/appViewsHandle/components/BillingViewHandle'));
    const LazyBillingContent = loadLazyComponent(() => import('app/billing/BillingContent'));
    const LazyPlansEdit = loadLazyComponent(() => import('app/billing/plans/PlansEdit'));
    const LazyPaymentView = loadLazyComponent(() => import('app/appViewsHandle/components/PaymentsViewHandle'));
    const LazyAccountManagerView = loadLazyComponent(() =>
        import('app/appViewsHandle/components/AccountManagerViewHandle'),
    );
    const LazyInstallationsView = loadLazyComponent(() =>
        import('app/appViewsHandle/components/InstallationsViewHandle'),
    );
    const LazyInstallationTable = loadLazyComponent(() => import('app/installation/InstallationTable'));
    const LazyTriagesView = loadLazyComponent(() => import('app/appViewsHandle/components/TriagesViewHandle'));
    const LazyContentsView = loadLazyComponent(() => import('app/appViewsHandle/components/ContentsViewHandle'));
    const LazyContentsTable = loadLazyComponent(() => import('app/content/ContentsTable'));

    const LazyLicenseView = loadLazyComponent(() => import('app/appViewsHandle/components/LicenseViewHandle'));
    const LazyAddOnsView = loadLazyComponent(() => import('app/appViewsHandle/components/AddOnsViewHandle'));
    const LazyCustomersView = loadLazyComponent(() => import('app/appViewsHandle/components/CustomersViewHandle'));
    const LazyCustomerTable = loadLazyComponent(() => import('app/customer/CustomerTable'));
    const LazyCustomerDetails = loadLazyComponent(() => import('app/customer/CustomerDetails'));
    const LazyVideoHelpView = loadLazyComponent(() => import('app/appViewsHandle/components/VideoHelpViewHandle'));
    const LazyCouponsView = loadLazyComponent(() => import('app/appViewsHandle/components/CouponsViewHandle'));
    const LazyCouponTable = loadLazyComponent(() => import('app/coupon/CouponTable'));
    const LazyCouponDetails = loadLazyComponent(() => import('app/coupon/CouponDetails'));
    const LazyCalendarGlobalView = loadLazyComponent(() =>
        import('app/appViewsHandle/components/CalendarGlobalViewHandle'),
    );
    const LazyReportConfigView = loadLazyComponent(() =>
        import('app/appViewsHandle/components/ReportConfigViewHandle'),
    );
    const LazyApplicationsView = loadLazyComponent(() =>
        import('app/appViewsHandle/components/ApplicationsViewHandle'),
    );
    const LazyApplicationTable = loadLazyComponent(() => import('app/application/ApplicationTable'));
    const LazyApplicationDetails = loadLazyComponent(() => import('app/application/ApplicationDetails'));
    // const LazyAdminDashboardsView = loadLazyComponent(() =>
    //     import('app/appViewsHandle/components/AdminDashboardsViewHandle'),
    // );
    // const LazyAdminDashboardTable = loadLazyComponent(() => import('app/adminDashboard/AdminDashboardTable'));
    // const LazyAdminDashboardDetails = loadLazyComponent(() => import('app/adminDashboard/AdminDashboardDetails'));
    const LazyTaxonomies = loadLazyComponent(() => import('app/appViewsHandle/components/TaxonomiesViewHandle'));
    const LazyReports = loadLazyComponent(() => import('app/appViewsHandle/components/ReportsViewHandle'));
    const LazyCommunications = loadLazyComponent(() => import('app/appViewsHandle/components/CommunicationViewHandle'));
    const LazyServicesView = loadLazyComponent(() => import('app/appViewsHandle/components/ServicesViewHandle'));
    const LazyServicesViewV4 = loadLazyComponent(() => import('app/appViewsHandle/components/ServicesViewHandleV4'));
    const LazyICalendarView = loadLazyComponent(() => import('app/appViewsHandle/components/ICalendarViewHandle'));
    const LazyAppointmentVerificationView = loadLazyComponent(() =>
        import('app/appViewsHandle/components/AppointmentVerificationView'),
    );
    const LazyOnliveDeliveryDashboard = loadLazyComponent(() =>
        import('app/dashboard/components/OnliveDeliveryDashboard'),
    );
    const LazySystemLogView = loadLazyComponent(() => import('app/appViewsHandle/components/SystemLogViewHandle'));
    const LazyVerticalManagement = loadLazyComponent(() =>
        import('app/appViewsHandle/components/VerticalManagementHandle'),
    );
    const LazyScriptsView = loadLazyComponent(() => import('app/appViewsHandle/components/ScriptsViewHandle'));

    const LazyIntegrationsView = loadLazyComponent(() =>
        import('app/appViewsHandle/components/IntegrationsViewHandle'),
    );

    const LazyBackgroundMFsView = loadLazyComponent(() =>
        import('app/appViewsHandle/components/BackgroundMFsViewHandle'),
    );

    const showInstallations = useCallback(
        (role) => {
            return (
                enableNewInstallations && (role === UserRole.ROOT || (role === UserRole.ADMIN && enableInstallations))
            );
        },
        [enableInstallations, enableNewInstallations],
    );

    const removeDisabledMenu = useCallback(
        (menuEntries) => {
            const onlyQrUser = hasOnlyPermission(user, UserPermissions.QR_CONFIRMATION);
            const sections = menuEntries.map((section) => {
                let { items, roles: sectionRoles, permissions: sectionPermissions } = section;
                items = items.filter((item) => {
                    const isRoot = user?.role === UserRole.ROOT;
                    const isHidden = typeof item.hide === 'function' ? item.hide(user) : item.hide;
                    if (isHidden) return false;

                    if (isRoot) return true;

                    const itemRoles = item?.roles?.length ? item.roles : sectionRoles || [];
                    const hasAccessRole = !itemRoles?.length || itemRoles.includes(user?.role);
                    if (!hasAccessRole) return false;

                    if (item?.authorizations) {
                        const isAuthorized = isAuthorize(item.authorization, item.mainPermission);
                        if (!isAuthorized) return false;
                    }

                    const itemPermissions = [...(item?.permissions || []), ...(sectionPermissions || [])];
                    const hasEmptyPermissions = Array.isArray(user?.permissions) && user.permissions.length === 0;
                    const isOrgAdmin = !user?.userGroupId;

                    // Users scoped exclusively to qrConfirmation only see items requiring it
                    if (onlyQrUser && !itemPermissions.includes(UserPermissions.QR_CONFIRMATION)) return false;

                    // For strict permissions, only root can bypass
                    // For non-strict permissions, orgAdmin and empty permissions array can also bypass
                    const isStrictPermission = item?.strictPermissions || section?.strictPermissions;
                    const shouldBypassPermissions = isStrictPermission
                        ? isRoot
                        : isRoot || (isOrgAdmin && hasEmptyPermissions);

                    if (itemPermissions?.length && !shouldBypassPermissions) {
                        const matchesPermission = itemPermissions.every((perm) => {
                            return userPermissions[perm]?.actions?.length > 0;
                        });
                        if (!matchesPermission) return false;
                    }

                    // Item has no permissions requirement, check hide logic
                    if (
                        !(item.path === '/billing' && license?.price === 0) &&
                        !(typeof item.hide === 'function' ? item.hide(user) : item.hide)
                    ) {
                        return true;
                    }

                    const isProfileHidden = item.hiddenIfProfile && organization?.isProfile;
                    if (isProfileHidden) return false;

                    return true;
                });
                return items.length > 0 ? { ...section, items } : null;
            });

            return sections.filter((section) => section && !section.hide);
        },
        [userPermissions, license, user, isAuthorize, organization?.isProfile],
    );

    return useMemo(
        () =>
            removeDisabledMenu([
                {
                    namespace: 'menu.events',
                    items: [
                        {
                            name: 'menu.oneToOne',
                            subTitle: 'menu.subTitle.oneToOne',
                            path: '/one-to-one',
                            component: (
                                <LazySuspense>
                                    <LazyOneToOneView />
                                </LazySuspense>
                            ),
                            icon: <ICallIcon />,
                            hide: hideO2O,
                        },

                        {
                            name: newIChat ? 'ichat.conversations' : 'menu.ichat',
                            subTitle: newIChat ? 'menu.subTitle.conversations' : 'menu.subTitle.ichat',
                            path: '/i-chat',
                            component: (
                                <LazySuspense>
                                    <LazyIChatView />
                                </LazySuspense>
                            ),
                            icon: <ChatBubbleOutline />,
                            hide: hideIChat,
                        },
                        {
                            name: 'menu.iCalendar',
                            subTitle: 'menu.subTitle.iCalendar',
                            path: '/i-calendar',
                            component:
                                Number(calendarVersion) === 4 ? (
                                    <MicroFront slug="icalendar" />
                                ) : (
                                    <LazySuspense>
                                        <LazyICalendarView />
                                    </LazySuspense>
                                ),
                            icon: <CalendarTodayOutlinedIcon />,
                            hide: hideICalendar,
                        },
                        {
                            name: 'menu.oneToMeny',
                            subTitle: 'menu.subTitle.oneToMany',
                            path: '/one-to-many',
                            component: (
                                <LazySuspense>
                                    <LazyOneToManyView />
                                </LazySuspense>
                            ),
                            icon: <ILiveIcon />,
                            hide: hideO2M,
                        },
                        {
                            name: 'menu.shoppables',
                            subTitle: 'menu.subTitle.shoppables',
                            path: '/shoppable',
                            component: (
                                <LazySuspense>
                                    <LazyShoppableView />
                                </LazySuspense>
                            ),
                            icon: <IVideoIcon />,
                            hide: hideShoppable,
                        },
                        {
                            name: 'menu.ievents',
                            subTitle: 'menu.subTitle.ievents',
                            path: '/i-events',
                            component: <MicroFront key="i-events" slug="ievents" />,
                            icon: <StarOutline />,
                            hide: hideIEvents,
                            permissions: ['ievents'],
                            strictPermissions: true,
                        },
                        {
                            name: 'menu.istore',
                            subTitle: 'menu.subTitle.istore',
                            path: '/i-store',
                            component: (
                                <MicroFront
                                    key="i-store"
                                    slug="ievents"
                                    query={{
                                        templateSlug: 'pre-booking',
                                        showSections: 'events,registrations,users,notifications',
                                    }}
                                />
                            ),
                            icon: <Storefront />,
                            hide: hideIStore,
                            permissions: ['istore'],
                            strictPermissions: true,
                        },
                    ],
                    roles: [UserRole.ADMIN, UserRole.EDITOR, UserRole.CREATOR],
                    section: 'events',
                },
                {
                    namespace: 'menu.contacts',
                    items: [
                        {
                            name: 'menu.contactList',
                            path: '/leads',
                            component: <MicroFront key="leads" slug="lead" />,
                            icon: <ContactsOutlinedIcon />,
                            hide: hideLeads,
                        },
                        {
                            name: 'menu.contactRequestList',
                            path: '/contact-requests',
                            component: <MicroFront key="contact-requests" slug="lead" path="contact-requests" />,
                            icon: <RecentActorsOutlinedIcon />,
                        },
                        {
                            name: 'menu.lostOpportunityList',
                            path: '/lost-opportunities',
                            component: <MicroFront key="lost-opportunities" slug="lead" path="lost" />,
                            icon: <PhoneMissedOutlinedIcon />,
                        },
                        {
                            name: 'menu.campaigns',
                            path: '/campaigns',
                            component: <MicroFront slug="lead" path="campaigns" />,
                            icon: <CampaignIcon />,
                            hide: hideCampaigns,
                        },
                    ],
                    collapsible: true,
                    roles: [UserRole.ADMIN, UserRole.EDITOR, UserRole.CREATOR],
                    hide: hideLeads,
                    permissions: ['lead'],
                },
                {
                    namespace: 'menu.assets',
                    items: [
                        {
                            name: 'menu.multimedia',
                            path: '/multimedia',
                            component: (
                                <LazySuspense>
                                    <LazyMultimediaView />
                                </LazySuspense>
                            ),
                            icon: <VideoLibraryOutlined />,
                            hide: hideMedia,
                        },
                        {
                            name: 'menu.engagement',
                            path: '/games',
                            component: (
                                <LazySuspense>
                                    <LazyEngagementView />
                                </LazySuspense>
                            ),
                            icon: <QuizOutlined />,
                            hide: hideInteractions,
                        },
                        {
                            name: 'menu.products',
                            path: '/products',
                            component:
                                organization?.provider === 'onlive' && !organization?.providerExternalSource ? (
                                    <MicroFront key="elements/products" slug="elements" path="products" />
                                ) : (
                                    <LazySuspense>
                                        <LazyProductsView />
                                    </LazySuspense>
                                ),
                            icon: <ShoppingCartOutlined />,
                        },
                    ],
                    roles: [UserRole.ADMIN, UserRole.EDITOR, UserRole.CREATOR],
                    collapsible: true,
                },
                {
                    namespace: 'menu.dashboards',
                    items: [
                        // {
                        //     name: 'menu.iCallDashboard',
                        //     path: '/o2o-dashboard',
                        //     component: (
                        //         <LazySuspense>
                        //             <LazyOneToOneDashboard />
                        //         </LazySuspense>
                        //     ),
                        //     icon: <BarChartOutlinedIcon />,
                        //     hide: hideO2O,
                        // },
                        {
                            name: 'menu.iCallMFDashboard',
                            path: '/dashboard-i-call',
                            component: <MicroFront key="dashboard-i-call" slug="dashboard" path="i-call" />,
                            icon: <BarChartOutlinedIcon />,
                            hide: hideO2O || user?.userGroupId,
                        },
                        {
                            name: 'menu.chatDashboard',
                            path: '/dashboard-i-chat',
                            component: <MicroFront key="dashboard-i-chat" slug="dashboard" path="i-chat" />,
                            icon: <LineAxisIcon />,
                            hide: hideIChat || user?.userGroupId || newIChat,
                        },
                        {
                            name: 'menu.iCalendarDashboard',
                            path: '/dashboard-i-calendar',
                            component: (
                                <MicroFront
                                    key="dashboard-i-calendar"
                                    slug="dashboard"
                                    path={Number(calendarVersion) === 4 ? 'i-calendar-v4' : 'i-calendar'}
                                />
                            ),
                            icon: <AssessmentOutlinedIcon />,
                            hide: hideICalendar,
                        },
                        {
                            name: 'menu.iEventDashboard',
                            path: '/dashboard-i-event',
                            component: <MicroFront key="dashboard-i-event" slug="dashboard" path="i-event" />,
                            icon: <StarRateOutlinedIcon />,
                            hide: hideIEvents && hideIStore,
                            permissions: ['ievents'],
                        },
                        // {
                        //     name: 'menu.openRateDashboard',
                        //     path: '/dashboard-open-rate',
                        //     component: <MicroFront key="dashboard-open-rate" slug="dashboard" path="open-rate" />,
                        //     icon: <AssessmentOutlinedIcon />,
                        //     hide: hideICalendar,
                        //     roles: ['admin'],
                        // },
                        // {
                        //     name: 'menu.iConversation',
                        //     path: '/dashboard-i-conversation',
                        //     component: <MicroFront key="dashboard-i-conversation" slug="dashboard" path="i-agent" />,
                        //     icon: <SupportAgentOutlinedIcon />,
                        //     hide: hideIChat || !newIChat,
                        // },
                        {
                            name: 'menu.iConversation',
                            path: '/dashboard-i-conversation',
                            component: (
                                <MicroFront key="dashboard-i-conversation" slug="dashboard" path="i-conversation" />
                            ),
                            icon: <SupportAgentOutlinedIcon />,
                            hide: hideIChat || !newIChat,
                        },
                        {
                            name: 'menu.immersiveDashboard',
                            path: '/dashboard-immersive',
                            component: <MicroFront key="dashboard-immersive" slug="dashboard" path="immersive" />,
                            icon: <SsidChartOutlinedIcon />,
                            hide: !isTestFeatureEnabled || hideImmersive,
                            roles: RootRole,
                        },
                        {
                            name: 'menu.immersiveOnDemandDashboard',
                            path: '/dashboard-on-demand',
                            component: (
                                <MicroFront key="dashboard-on-demand" slug="dashboard" path="immersive-on-demand" />
                            ),
                            icon: <AutoGraphOutlinedIcon />,
                            hide: hideImmersive,
                        },
                        {
                            name: 'menu.leadDashboard',
                            path: '/dashboard-leads',
                            component: <MicroFront key="dashboard-leads" slug="dashboard" path="leads" />,
                            icon: <QueryStatsOutlinedIcon />,
                            hide: hideLeads,
                            permissions: ['lead'],
                        },
                    ],
                    roles: [UserRole.ADMIN, UserRole.EDITOR],
                    collapsible: true,
                    hide:
                        (hideIChat &&
                            hideO2O &&
                            hideImmersive &&
                            hideLeads &&
                            hideICalendar &&
                            hideIEvents &&
                            hideIStore) ||
                        hideDashboard,
                },
                {
                    namespace: 'menu.channels',
                    items: [
                        // {
                        //     name: 'menu.yourWeb',
                        //     path: '/web-tv-channel',
                        //     component: <WebTvChannelViewHandle />,
                        //     icon: <TvOutlinedIcon />,
                        //     hide: hideTvWidget,
                        // },
                        // {
                        //     name: 'menu.widgets',
                        //     path: '/widgets',
                        //     component: <WidgetsViewHandle />,
                        //     icon: <SmartButtonOutlinedIcon />,
                        // },
                        // {
                        //     name: 'menu.agentPage',
                        //     path: '/agent',
                        //     component: <AgentPageViewHandle />,
                        //     icon: <CoPresentOutlinedIcon />,
                        // },
                        {
                            name: 'menu.socialMedia',
                            path: '/social-media',
                            component: (
                                <LazySuspense>
                                    <LazySocialMediaView />
                                </LazySuspense>
                            ),
                            icon: <ThumbUpOffAltOutlinedIcon />,
                            hide: hideChannel,
                        },
                        {
                            name: 'menu.tvChannels',
                            path: '/tv-channels',
                            component: (
                                <LazySuspense>
                                    <LazyTvChannelView />
                                </LazySuspense>
                            ),
                            icon: <DesktopMacOutlinedIcon />,
                            childrenRoutes: (
                                <React.Fragment>
                                    <Route
                                        index={true}
                                        element={
                                            <LazySuspense>
                                                <LazyTvChannelsConnectors />
                                            </LazySuspense>
                                        }
                                    />
                                    <Route
                                        exact
                                        path=":channelId"
                                        element={
                                            <LazySuspense>
                                                <LazyEditTvChannelView />
                                            </LazySuspense>
                                        }
                                    />
                                </React.Fragment>
                            ),
                            hide: hideChannel,
                        },
                    ],
                    roles: AdminRole,
                    collapsible: true,
                    hide: hideChannel,
                },
                {
                    namespace: 'menu.configuration',
                    items: [
                        {
                            name: 'menu.user',
                            path: '/users',
                            component: (
                                <LazySuspense>
                                    <LazyUsersView />
                                </LazySuspense>
                            ),
                            icon: <ManageAccountsOutlinedIcon />,
                        },
                        {
                            name: 'menu.billing',
                            path: '/billing',
                            component: (
                                <LazySuspense>
                                    <LazyBillingView />
                                </LazySuspense>
                            ),
                            icon: <PaymentsOutlined />,
                            childrenRoutes: (
                                <React.Fragment>
                                    <Route
                                        index={true}
                                        element={
                                            <LazySuspense>
                                                <LazyBillingContent />
                                            </LazySuspense>
                                        }
                                    />
                                    <Route
                                        exact
                                        path=":edit-plan"
                                        element={
                                            <LazySuspense>
                                                <LazyPlansEdit />
                                            </LazySuspense>
                                        }
                                    />
                                </React.Fragment>
                            ),
                            hiddenIfProfile: true,
                            roles: AdminRole,
                            hide: (user) => user?.role !== UserRole.ROOT && hideLicense,
                        },
                        {
                            name: 'menu.accounts',
                            path: '/accounts',
                            component: (
                                <LazySuspense>
                                    <LazyAccountManagerView />
                                </LazySuspense>
                            ),
                            icon: <BusinessOutlined />,
                            authorizations: ['multiAccount', 'multiProfile'],
                            mainPermission: true,
                            roles: AdminRole,
                            hide: (user) => {
                                return user?.userGroupId;
                            },
                        },
                        {
                            name: 'Payments',
                            path: '/payments',
                            component: (
                                <LazySuspense>
                                    <LazyPaymentView />
                                </LazySuspense>
                            ),
                            icon: <PaymentsOutlined />,
                            roles: AdminRole,
                            hide: !isTestFeatureEnabled || !isEnableTestDriveStripePayments,
                        },

                        {
                            name: 'menu.installations',
                            path: '/installations',
                            component: (
                                <LazySuspense>
                                    <LazyInstallationsView />
                                </LazySuspense>
                            ),
                            childrenRoutes: (
                                <Route
                                    index={true}
                                    element={
                                        <LazySuspense>
                                            <LazyInstallationTable />
                                        </LazySuspense>
                                    }
                                />
                            ),
                            icon: <InstallDesktop />,
                            roles: AdminRole,
                            hide: (user) => {
                                return !showInstallations(user?.role);
                            },
                        },
                        {
                            name: 'menu.triages',
                            path: '/triages',
                            component: (
                                <LazySuspense>
                                    <LazyTriagesView />
                                </LazySuspense>
                            ),
                            icon: <AccountTreeOutlinedIcon />,
                            roles: RootRole,
                        },

                        {
                            name: 'menu.contents',
                            path: '/contents',
                            component: (
                                <LazySuspense>
                                    <LazyContentsView />
                                </LazySuspense>
                            ),
                            childrenRoutes: (
                                <Route
                                    index={true}
                                    element={
                                        <LazySuspense>
                                            <LazyContentsTable />
                                        </LazySuspense>
                                    }
                                />
                            ),
                            icon: <Article />,
                            roles: RootRole,
                            hide: !isTestFeatureEnabled,
                        },

                        // {
                        //     name: 'menu.qaRecords',
                        //     path: '/qa-records',
                        //     component: (
                        //         <LazySuspense>
                        //             <LazyShoppableView qaMode={true} />
                        //         </LazySuspense>
                        //     ),
                        //     icon: <VoicemailOutlinedIcon />,
                        //     hide: hideO2O,
                        //     roles: AdminRole,
                        // },
                        {
                            name: 'menu.webhooks',
                            path: '/webhooks',
                            component: (
                                <LazySuspense>
                                    <LazyWebhooksView />
                                </LazySuspense>
                            ),
                            icon: <WebhookOutlined />,
                            hide: !isTestFeatureEnabled,
                            roles: AdminRole,
                        },
                        {
                            name: 'profile.settings.genaiSettings.genai',
                            path: '/genai',
                            component: <MicroFront key="genai" slug="genai" />,
                            icon: <GenAIcon />,
                            hide: !isTestFeatureEnabled || !genaiEnabled,
                            roles: AdminRole,
                        },
                        {
                            name: 'menu.forms',
                            path: '/forms',
                            component: <MicroFront key="forms" slug="forms" />,
                            icon: <ListAltOutlined />,
                            hide: !isTestFeatureEnabled,
                            roles: AdminRole,
                        },
                        {
                            name: 'menu.measureTargets',
                            path: '/measure-targets',
                            component: <MicroFront key="measure-targets" slug="dashboard" path="measure-targets" />,
                            icon: <BarChartOutlinedIcon />,
                            hide: user?.userGroupId,
                            roles: AdminRole,
                        },
                        {
                            name: 'menu.services',
                            path: '/services',
                            component: (
                                <LazySuspense>
                                    <LazyServicesView />
                                </LazySuspense>
                            ),
                            icon: <DatasetOutlinedIcon />,
                            hide: hideICalendar || Number(calendarVersion) !== 3,
                            permissions: ['service'],
                        },
                        {
                            name: 'menu.services',
                            path: '/v4-services',
                            component: (
                                <LazySuspense>
                                    <LazyServicesViewV4 />
                                </LazySuspense>
                            ),
                            icon: <DatasetOutlinedIcon />,
                            hide: hideICalendar || Number(calendarVersion) !== 4,
                            permissions: ['service'],
                        },
                        {
                            name: 'menu.taxonomy',
                            path: '/taxonomies',
                            component: (
                                <LazySuspense>
                                    <LazyTaxonomies />
                                </LazySuspense>
                            ),
                            icon: <DeviceHubOutlinedIcon />,
                            roles: RootRole,
                        },
                        {
                            name: 'menu.reports',
                            path: '/reports',
                            component: (
                                <LazySuspense>
                                    <LazyReports />
                                </LazySuspense>
                            ),
                            icon: <SummarizeOutlinedIcon />,
                        },
                        {
                            name: 'menu.communications',
                            path: '/communications',
                            component: (
                                <LazySuspense>
                                    <LazyCommunications />
                                </LazySuspense>
                            ),
                            icon: <CommunicationIcon />,
                            roles: AdminRole,
                            // hide: (user) => {
                            //     return user?.userGroupId;
                            // },
                        },
                        {
                            name: 'menu.verification',
                            path: '/appointment-verification',
                            component: (
                                <LazySuspense>
                                    <LazyAppointmentVerificationView />
                                </LazySuspense>
                            ),
                            hide: hideICalendar,
                            icon: <QrCode />,
                        },
                        {
                            name: 'ichat.conversations',
                            path: '/conversations',
                            component: <MicroFront key="conversations" slug="ichat" path="meta" />,
                            icon: <ConversationIcon />,
                            hide: hideIChat || !isTestFeatureEnabled,
                            roles: AdminRole,
                        },
                        {
                            name: 'app.resource.qrConfirmation',
                            path: '/qr-confirmation',
                            component: <QrConfirmation />,
                            icon: <QrCode />,
                            permissions: ['qrConfirmation'],
                            strictPermissions: true,
                        },
                    ],
                    roles: [UserRole.ROOT, UserRole.ADMIN, UserRole.CREATOR, UserRole.EDITOR],
                    collapsible: true,
                },
                {
                    namespace: 'menu.manage',
                    items: [
                        {
                            name: 'menu.license',
                            path: '/licenses',
                            component: (
                                <LazySuspense>
                                    <LazyLicenseView />
                                </LazySuspense>
                            ),
                            icon: <KeyOutlined />,
                            roles: RootRole,
                        },
                        {
                            name: 'menu.AddOns',
                            path: '/add-ons',
                            component: (
                                <LazySuspense>
                                    <LazyAddOnsView />
                                </LazySuspense>
                            ),
                            icon: <SettingsApplicationsOutlined />,
                            roles: RootRole,
                        },
                        {
                            name: 'menu.customers',
                            path: '/customers',
                            component: (
                                <LazySuspense>
                                    <LazyCustomersView />
                                </LazySuspense>
                            ),
                            childrenRoutes: (
                                <React.Fragment>
                                    <Route
                                        index={true}
                                        element={
                                            <LazySuspense>
                                                <LazyCustomerTable />
                                            </LazySuspense>
                                        }
                                    />
                                    <Route
                                        path=":customerId"
                                        element={
                                            <LazySuspense>
                                                <LazyCustomerDetails />
                                            </LazySuspense>
                                        }
                                    />
                                </React.Fragment>
                            ),
                            icon: <BusinessCenterOutlined />,
                            roles: RootRole,
                        },
                        {
                            name: 'menu.videoHelp',
                            path: '/video-help',
                            component: (
                                <LazySuspense>
                                    <LazyVideoHelpView />
                                </LazySuspense>
                            ),
                            icon: <HelpOutlineOutlined />,
                            roles: RootRole,
                        },
                        {
                            name: 'menu.coupons',
                            path: '/coupons',
                            component: (
                                <LazySuspense>
                                    <LazyCouponsView />
                                </LazySuspense>
                            ),
                            childrenRoutes: (
                                <React.Fragment>
                                    <Route
                                        index={true}
                                        element={
                                            <LazySuspense>
                                                <LazyCouponTable />
                                            </LazySuspense>
                                        }
                                    />
                                    <Route
                                        exact
                                        path=":couponId"
                                        element={
                                            <LazySuspense>
                                                <LazyCouponDetails />
                                            </LazySuspense>
                                        }
                                    />
                                </React.Fragment>
                            ),
                            icon: <DiscountOutlined />,
                            roles: RootRole,
                        },
                        {
                            name: 'menu.calendarGlobal',
                            path: '/general-calendar',
                            component: (
                                <LazySuspense>
                                    <LazyCalendarGlobalView />
                                </LazySuspense>
                            ),
                            icon: <EventOutlined />,
                            roles: RootRole,
                        },
                        {
                            name: 'menu.formsGeneral',
                            path: '/general-forms',
                            component: <MicroFront key="general-forms" slug="forms" path="root" />,
                            icon: <ListAltOutlined />,
                            roles: RootRole,
                        },
                        {
                            name: 'menu.reportConfig',
                            path: '/report-config',
                            component: (
                                <LazySuspense>
                                    <LazyReportConfigView />
                                </LazySuspense>
                            ),
                            icon: <SaveAsOutlinedIcon />,
                            roles: RootRole,
                        },
                        {
                            name: 'menu.admin.scripts',
                            path: '/admin-scripts',
                            component: (
                                <LazySuspense>
                                    <LazyScriptsView />
                                </LazySuspense>
                            ),
                            icon: <CodeOutlined />,
                            roles: RootRole,
                        },
                        {
                            name: 'menu.applications',
                            path: '/applications',
                            component: (
                                <LazySuspense>
                                    <LazyApplicationsView />
                                </LazySuspense>
                            ),
                            childrenRoutes: (
                                <React.Fragment>
                                    <Route
                                        index={true}
                                        element={
                                            <LazySuspense>
                                                <LazyApplicationTable />
                                            </LazySuspense>
                                        }
                                    />
                                    <Route
                                        exact
                                        path=":applicationId"
                                        element={
                                            <LazySuspense>
                                                <LazyApplicationDetails />
                                            </LazySuspense>
                                        }
                                    />
                                </React.Fragment>
                            ),
                            icon: <Apps />,
                            roles: RootRole,
                        },
                        /*
                    {
                        name: 'menu.admin.dashboards',
                        path: '/admin-dashboards',
                        component: (
                            <LazySuspense>
                                <LazyAdminDashboardsView />
                            </LazySuspense>
                        ),
                        childrenRoutes: (
                            <React.Fragment>
                                <Route
                                    index={true}
                                    element={
                                        <LazySuspense>
                                            <LazyAdminDashboardTable />
                                        </LazySuspense>
                                    }
                                />
                                <Route
                                    exact
                                    path=":adminDashboardId"
                                    element={
                                        <LazySuspense>
                                            <LazyAdminDashboardDetails />
                                        </LazySuspense>
                                    }
                                />
                            </React.Fragment>
                        ),
                        icon: <PieChartOutline />,
                    },
                    */
                        {
                            name: 'analytics.shoppable.tabs.delivery',
                            path: '/delivery',
                            component: (
                                <LazySuspense>
                                    <LazyOnliveDeliveryDashboard />
                                </LazySuspense>
                            ),
                            icon: <DeliveryIcon />,
                            roles: RootRole,
                        },
                        {
                            name: 'menu.systemLogAuth',
                            path: '/system-logs',
                            component: (
                                <LazySuspense>
                                    <LazySystemLogView />
                                </LazySuspense>
                            ),
                            icon: <EventNoteOutlined />,
                            roles: RootRole,
                        },
                        {
                            name: 'menu.verticalManagement',
                            path: '/vertical-management',
                            component: (
                                <LazySuspense>
                                    <LazyVerticalManagement />
                                </LazySuspense>
                            ),
                            icon: <CategoryOutlinedIcon />,
                            roles: RootRole,
                        },
                        {
                            name: 'menu.genaiManagement',
                            path: '/genai/administration',
                            component: <MicroFront key="genai/administration" slug="genai" path="administration" />,
                            icon: <GenAIcon />,
                            roles: RootRole,
                        },
                        {
                            name: 'menu.integrations',
                            path: '/integrations',
                            component: (
                                <LazySuspense>
                                    <LazyIntegrationsView />
                                </LazySuspense>
                            ),
                            icon: <IntegrationInstructionsOutlined />,
                            roles: RootRole,
                        },
                        {
                            name: 'menu.streaming',
                            path: '/streaming',
                            component: <MicroFront key="streaming" slug="streaming" />,
                            icon: <StreamingIcon />,
                            roles: RootRole,
                        },
                        {
                            name: 'menu.backgroundMFs',
                            path: '/background-mfs',
                            component: (
                                <LazySuspense>
                                    <LazyBackgroundMFsView />
                                </LazySuspense>
                            ),
                            icon: <BackgroundMFsIcon />,
                            hide: !isTestFeatureEnabled,
                        },
                    ],
                    roles: [UserRole.ROOT, UserRole.ADMIN],
                    collapsible: true,
                },
            ]),
        [
            hideO2O,
            newIChat,
            hideIChat,
            hideICalendar,
            calendarVersion,
            hideO2M,
            hideShoppable,
            hideIEvents,
            hideIStore,
            hideLeads,
            hideCampaigns,
            hideMedia,
            hideInteractions,
            isTestFeatureEnabled,
            hideImmersive,
            hideDashboard,
            hideChannel,
            hideLicense,
            genaiEnabled,
            showInstallations,
            removeDisabledMenu,
            user?.userGroupId,
            organization?.provider,
            organization?.providerExternalSource,
            isEnableTestDriveStripePayments,
        ],
    );
}
